import React, { useEffect, useState } from "react";
import { parseEther, formatEther } from "viem";
import { useAccount, usePublicClient, useWriteContract, useWaitForTransactionReceipt, useBalance } from "wagmi";
import { ArrowDown, Wallet, ExternalLink, Clock } from "lucide-react";
import { getTokenInfo } from "@/api";
import { cronosMainnet } from "@/wagmi";
import { useApp, ConnectPrompt } from "@/App";
import { addFlameToWallet } from "@/components/CopyButton";

const ROUTER_ABI = [{
  type: "function", name: "exactInputSingle", stateMutability: "payable",
  inputs: [{ name: "params", type: "tuple", components: [
    { name: "tokenIn", type: "address" }, { name: "tokenOut", type: "address" }, { name: "fee", type: "uint24" },
    { name: "recipient", type: "address" }, { name: "deadline", type: "uint256" }, { name: "amountIn", type: "uint256" },
    { name: "amountOutMinimum", type: "uint256" }, { name: "sqrtPriceLimitX96", type: "uint160" }] }],
  outputs: [{ name: "amountOut", type: "uint256" }],
}];

const fmt = (wei) => Number(formatEther(wei)).toLocaleString(undefined, { maximumFractionDigits: 2 });

function useQuote(t, address, amount) {
  const client = usePublicClient({ chainId: cronosMainnet.id });
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setQuote(null); setErr("");
    const n = Number(amount);
    if (!t?.pool?.exists || !address || !n || n <= 0) return undefined;
    const id = setTimeout(async () => {
      try {
        const value = parseEther(String(n));
        const { result } = await client.simulateContract({
          address: t.dex.router, abi: ROUTER_ABI, functionName: "exactInputSingle", account: address, value,
          args: [{ tokenIn: t.dex.wcro, tokenOut: t.contracts.flame, fee: t.dex.fee, recipient: address,
            deadline: BigInt(Math.floor(Date.now() / 1000) + 900), amountIn: value, amountOutMinimum: 0n, sqrtPriceLimitX96: 0n }],
        });
        setQuote(result);
      } catch (e) { setErr(e.shortMessage || "Quote unavailable (check your CRO balance)"); }
    }, 400);
    return () => clearTimeout(id);
  }, [t, address, amount, client]);
  return { quote, err };
}

export default function BuyPage() {
  const { address, isConnected } = useAccount();
  const { showToast } = useApp();
  const [t, setT] = useState(null);
  const [amount, setAmount] = useState("10");
  const [slippage, setSlippage] = useState("1");
  const { data: bal } = useBalance({ address, chainId: cronosMainnet.id, query: { enabled: !!address } });
  const { quote, err } = useQuote(t, address, amount);
  const { writeContract, data: hash, isPending, error } = useWriteContract();
  const receipt = useWaitForTransactionReceipt({ hash });

  useEffect(() => { getTokenInfo().then(setT); }, []);
  useEffect(() => { if (receipt.isSuccess) { showToast("Swap confirmed. FLAME is in your wallet"); getTokenInfo().then(setT); } }, [receipt.isSuccess]); // eslint-disable-line

  if (!isConnected) return <ConnectPrompt />;
  if (!t) return <div className="pt-32 text-center text-slate-400">Loading…</div>;

  const swap = () => {
    const value = parseEther(String(Number(amount)));
    const minOut = (quote * BigInt(Math.round((100 - Number(slippage)) * 100))) / 10000n;
    showToast("Confirm the swap in your wallet…");
    writeContract({
      address: t.dex.router, abi: ROUTER_ABI, functionName: "exactInputSingle", value, chainId: cronosMainnet.id,
      args: [{ tokenIn: t.dex.wcro, tokenOut: t.contracts.flame, fee: t.dex.fee, recipient: address,
        deadline: BigInt(Math.floor(Date.now() / 1000) + 900), amountIn: value, amountOutMinimum: minOut, sqrtPriceLimitX96: 0n }],
    });
  };

  return (
    <div className="pt-24 pb-20">
      <div className="max-w-md mx-auto px-4">
        <h1 className="font-display text-4xl font-black flex items-center gap-3"><img src="/brand/flame-64.png" alt="" className="w-10 h-10" /> Buy FLAME</h1>
        <div className="mt-2 text-sm text-slate-400" data-testid="buy-price">
          {t.pool.exists ? <>1 FLAME = <span className="font-mono text-orange-300">{t.pool.price_cro?.toPrecision(4)} CRO</span> · live from VVS V3</> : "Pool not live yet"}
        </div>
        {!t.pool.exists ? (
          <div className="mt-6 card-tile card-flame text-center py-10" data-testid="buy-trading-soon">
            <Clock className="mx-auto text-amber-400" size={36} />
            <div className="font-display text-2xl font-bold mt-3">Trading opens soon</div>
            <p className="text-sm text-slate-400 mt-2">The FLAME/CRO pool on VVS Finance V3 launches shortly. You can already add FLAME to your wallet.</p>
          </div>
        ) : (
          <div className="mt-6 card-tile card-flame" data-testid="buy-swap-card">
            <div className="rounded-xl bg-black/40 border border-white/10 p-4">
              <div className="flex justify-between text-xs text-slate-400"><span>You pay</span><span>Balance {bal ? Number(bal.formatted).toFixed(2) : "—"} CRO</span></div>
              <div className="flex items-center gap-2 mt-1">
                <input data-testid="buy-amount-input" type="number" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-transparent text-3xl font-display font-black w-full outline-none" />
                <span className="chip">CRO</span>
              </div>
            </div>
            <div className="flex justify-center -my-2 relative z-10"><div className="rounded-full bg-[#181A26] border border-white/10 p-1.5"><ArrowDown size={16} /></div></div>
            <div className="rounded-xl bg-black/40 border border-white/10 p-4">
              <div className="text-xs text-slate-400">You receive (estimated)</div>
              <div className="flex items-center gap-2 mt-1">
                <div className="text-3xl font-display font-black text-orange-300 w-full" data-testid="buy-quote">{quote ? fmt(quote) : "—"}</div>
                <span className="chip">FLAME</span>
              </div>
            </div>
            {err && <div className="text-xs text-rose-400 mt-2" data-testid="buy-quote-error">{err}</div>}
            <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">Slippage
              {["0.5", "1", "3"].map((s) => (
                <button key={s} data-testid={`buy-slippage-${s}`} onClick={() => setSlippage(s)} className={`px-2 py-1 rounded-lg border ${slippage === s ? "border-orange-500 text-orange-300" : "border-white/10"}`}>{s}%</button>
              ))}
            </div>
            <button data-testid="buy-swap-button" onClick={swap} disabled={!quote || isPending || receipt.isLoading} className="btn-tactile btn-flame w-full mt-4 font-display">
              {isPending ? "Confirm in wallet…" : receipt.isLoading ? "Swapping…" : "Swap CRO for FLAME"}
            </button>
            {hash && <a className="text-xs text-cyan-400 underline mt-2 inline-block" href={`https://explorer.cronos.org/tx/${hash}`} target="_blank" rel="noreferrer">View tx ↗</a>}
            {error && <div className="text-xs text-rose-400 mt-2">{error.shortMessage || error.message}</div>}
          </div>
        )}
        <div className="mt-4 flex flex-wrap gap-2">
          <button data-testid="buy-add-wallet" onClick={() => addFlameToWallet(t.contracts.flame).catch((e) => showToast(e.message, "error"))} className="btn-tactile btn-ghost text-sm flex items-center gap-1"><Wallet size={14} /> Add FLAME to wallet</button>
          {t.pool.exists && <>
            <a href={`https://dexscreener.com/cronos/${t.pool.address}`} target="_blank" rel="noreferrer" className="btn-tactile btn-ghost text-sm" data-testid="buy-dexscreener">Chart <ExternalLink size={12} className="inline" /></a>
            <a href={`https://vvs.finance/swap?outputCurrency=${t.contracts.flame}`} target="_blank" rel="noreferrer" className="btn-tactile btn-ghost text-sm" data-testid="buy-vvs">Trade on VVS <ExternalLink size={12} className="inline" /></a>
          </>}
        </div>
      </div>
    </div>
  );
}
